// Shared visual language for HUD cards: dark glass panel, subtle border, soft shadow.
import { getClass, ARENA, OBSTACLES, COOP } from "@tank/shared";

function panel(styles: Partial<CSSStyleDeclaration> = {}): HTMLDivElement {
  const el = document.createElement("div");
  Object.assign(el.style, {
    position: "fixed", zIndex: "10",
    fontFamily: "system-ui, sans-serif", color: "#fff",
    background: "rgba(10,14,18,0.58)", backdropFilter: "blur(4px)",
    border: "1px solid rgba(255,255,255,0.08)", borderRadius: "10px",
    padding: "10px 14px", boxShadow: "0 4px 16px rgba(0,0,0,0.35)",
  } satisfies Partial<CSSStyleDeclaration>);
  Object.assign(el.style, styles);
  return el;
}

/** Small pill-shaped stat readout (e.g. "3 kills"). */
function chip(): HTMLSpanElement {
  const el = document.createElement("span");
  Object.assign(el.style, {
    display: "inline-flex", alignItems: "center", gap: "4px",
    background: "rgba(255,255,255,0.08)", borderRadius: "999px",
    padding: "2px 9px", fontSize: "12px", opacity: ".92",
  });
  return el;
}

/** In-game DOM HUD: HP, score, player list, objective, respawn, round end. */
export class HUD {
  private hpFill: HTMLElement;
  private hpText: HTMLElement;
  private killsChip: HTMLElement;
  private scoreChip: HTMLElement;
  private center: HTMLElement;
  private playerList: HTMLElement;
  private objective: HTMLElement;
  private respawn: HTMLElement;
  private code: HTMLElement;
  private copyBtn: HTMLButtonElement;
  private roundEnd: HTMLElement;
  private roomCode = "";
  // Ability cooldown card (bottom-center).
  private abilityWrap: HTMLElement;
  private abilityIcon: HTMLElement;
  private abilityKey: HTMLElement;
  private abilityName: HTMLElement;
  private abilityState: HTMLElement;
  private minimapCanvas: HTMLCanvasElement;
  private minimapCtx: CanvasRenderingContext2D;
  private waveBanner: HTMLElement;
  private waveBannerMain: HTMLElement;
  private waveBannerSub: HTMLElement;
  private waveBannerTimer: ReturnType<typeof setTimeout> | undefined;

  constructor() {
    this.hpFill = document.createElement("div");
    this.hpText = document.createElement("div");
    this.killsChip = chip();
    this.scoreChip = chip();
    this.playerList = document.createElement("div");
    this.objective = document.createElement("div");
    this.respawn = document.createElement("div");
    this.code = document.createElement("span");
    this.roundEnd = document.createElement("div");
    this.abilityWrap = document.createElement("div");
    this.abilityIcon = document.createElement("div");
    this.abilityKey = document.createElement("div");
    this.abilityName = document.createElement("div");
    this.abilityState = document.createElement("div");
    this.minimapCanvas = document.createElement("canvas");
    this.minimapCtx = this.minimapCanvas.getContext("2d")!;
    this.waveBanner = document.createElement("div");
    this.waveBannerMain = document.createElement("div");
    this.waveBannerSub = document.createElement("div");

    // Bottom-left: HP bar + stat chips (kills / score), in its own card.
    const bottomLeft = panel({ left: "12px", bottom: "12px" });
    const bar = document.createElement("div");
    Object.assign(bar.style, { width: "220px", height: "14px", background: "#1c2730", borderRadius: "7px", overflow: "hidden" });
    Object.assign(this.hpFill.style, { width: "100%", height: "100%", background: "linear-gradient(90deg,#e63946,#f4a261)", transition: "width .12s" });
    bar.appendChild(this.hpFill);
    Object.assign(this.hpText.style, { fontSize: "13px", fontWeight: "600", marginTop: "6px" });
    const statsRow = document.createElement("div");
    Object.assign(statsRow.style, { display: "flex", gap: "6px", marginTop: "6px" });
    this.killsChip.textContent = "0 kills";
    this.scoreChip.textContent = "0 pts";
    statsRow.appendChild(this.killsChip);
    statsRow.appendChild(this.scoreChip);
    bottomLeft.appendChild(bar);
    bottomLeft.appendChild(this.hpText);
    bottomLeft.appendChild(statsRow);
    document.body.appendChild(bottomLeft);

    // Top-left: objective + room code (with a copy button).
    const topLeft = panel({ left: "12px", top: "12px" });
    Object.assign(this.objective.style, { fontSize: "15px", fontWeight: "600" });
    const codeRow = document.createElement("div");
    Object.assign(codeRow.style, { display: "flex", alignItems: "center", gap: "6px", marginTop: "4px" });
    Object.assign(this.code.style, { fontSize: "12px", opacity: ".7", fontFamily: "monospace" });
    this.copyBtn = document.createElement("button");
    this.copyBtn.textContent = "Copy";
    this.copyBtn.title = "Copy room code";
    Object.assign(this.copyBtn.style, {
      fontSize: "11px", padding: "2px 8px", borderRadius: "6px", border: "1px solid rgba(255,255,255,0.15)",
      background: "rgba(255,255,255,0.08)", color: "#fff", cursor: "pointer", display: "none",
    });
    this.copyBtn.addEventListener("click", () => this.copyRoomCode());
    codeRow.appendChild(this.code);
    codeRow.appendChild(this.copyBtn);
    topLeft.appendChild(this.objective);
    topLeft.appendChild(codeRow);
    document.body.appendChild(topLeft);

    // Top-right: player list.
    const topRight = panel({ top: "12px", right: "12px", padding: "8px 12px" });
    Object.assign(this.playerList.style, {
      fontFamily: "monospace", fontSize: "12px", color: "#cdd6dd", textAlign: "right", lineHeight: "1.6",
    });
    topRight.appendChild(this.playerList);
    document.body.appendChild(topRight);

    // Bottom-right: minimap (arena bounds, obstacles, power-ups, tanks).
    const mapPanel = panel({ right: "12px", bottom: "12px", padding: "6px" });
    const mapSize = 160;
    this.minimapCanvas.width = mapSize;
    this.minimapCanvas.height = mapSize;
    Object.assign(this.minimapCanvas.style, { display: "block", borderRadius: "6px" });
    mapPanel.appendChild(this.minimapCanvas);
    document.body.appendChild(mapPanel);

    // Center: respawn / round end.
    this.center = document.createElement("div");
    Object.assign(this.center.style, {
      position: "fixed", left: "50%", top: "32%", transform: "translateX(-50%)", zIndex: "11",
      fontFamily: "system-ui, sans-serif", color: "#fff", textAlign: "center", textShadow: "0 2px 6px #000",
    });
    Object.assign(this.respawn.style, { fontSize: "22px", fontWeight: "600", display: "none" });
    Object.assign(this.roundEnd.style, {
      fontSize: "30px", fontWeight: "700", display: "none", padding: "20px 30px",
      background: "rgba(8,12,16,.8)", borderRadius: "10px",
    });
    this.center.appendChild(this.respawn);
    this.center.appendChild(this.roundEnd);
    document.body.appendChild(this.center);

    // Wave announcement (Coop only): a brief, fading banner above center so it
    // doesn't collide with the respawn/round-end text. Opacity-transitioned
    // rather than display-toggled so it can fade in and back out.
    Object.assign(this.waveBanner.style, {
      position: "fixed", left: "50%", top: "18%", transform: "translateX(-50%)", zIndex: "11",
      fontFamily: "system-ui, sans-serif", color: "#fff", textAlign: "center", textShadow: "0 2px 6px #000",
      fontSize: "34px", fontWeight: "700", letterSpacing: "3px",
      opacity: "0", transition: "opacity .4s", pointerEvents: "none",
    });
    Object.assign(this.waveBannerSub.style, { fontSize: "15px", fontWeight: "500", letterSpacing: "1px", marginTop: "4px", color: "#e76f51" });
    this.waveBanner.appendChild(this.waveBannerMain);
    this.waveBanner.appendChild(this.waveBannerSub);
    document.body.appendChild(this.waveBanner);

    // Bottom-center: class-ability cooldown card. The icon is a radial sweep
    // (conic-gradient) that fills as the special recharges; the key hint sits
    // on top, name + countdown below.
    Object.assign(this.abilityWrap.style, {
      position: "fixed", left: "50%", bottom: "16px", transform: "translateX(-50%)", zIndex: "12",
      display: "none", alignItems: "center", gap: "10px",
      fontFamily: "system-ui, sans-serif", color: "#fff",
      background: "rgba(10,14,18,0.58)", backdropFilter: "blur(4px)",
      border: "1px solid rgba(255,255,255,0.08)", borderRadius: "10px",
      padding: "8px 14px 8px 8px", boxShadow: "0 4px 16px rgba(0,0,0,0.35)",
    });
    const ring = document.createElement("div");
    Object.assign(ring.style, { width: "50px", height: "50px", borderRadius: "50%", position: "relative", flex: "0 0 auto" });
    Object.assign(this.abilityIcon.style, {
      position: "absolute", inset: "4px", borderRadius: "50%",
      background: "conic-gradient(#2a9d8f 100%, rgba(255,255,255,0.08) 100%)",
      transition: "background .05s linear",
    });
    const inner = document.createElement("div");
    Object.assign(inner.style, { position: "absolute", inset: "6px", borderRadius: "50%", background: "#0e151b", display: "flex", alignItems: "center", justifyContent: "center" });
    Object.assign(this.abilityKey.style, { fontSize: "11px", fontWeight: "700", letterSpacing: "0.5px", color: "#e9eef2" });
    this.abilityKey.textContent = "SHIFT";
    inner.appendChild(this.abilityKey);
    ring.appendChild(this.abilityIcon);
    ring.appendChild(inner);
    const meta = document.createElement("div");
    Object.assign(meta.style, { display: "flex", flexDirection: "column", lineHeight: "1.25" });
    Object.assign(this.abilityName.style, { fontSize: "14px", fontWeight: "600" });
    Object.assign(this.abilityState.style, { fontSize: "12px", fontWeight: "600", color: "#2a9d8f" });
    this.abilityState.id = "hud-ability-state";
    this.abilityState.textContent = "READY";
    meta.appendChild(this.abilityName);
    meta.appendChild(this.abilityState);
    this.abilityWrap.appendChild(ring);
    this.abilityWrap.appendChild(meta);
    document.body.appendChild(this.abilityWrap);
  }

  setRoomCode(code: string, mode: string): void {
    this.roomCode = code;
    this.code.textContent = `${mode.toUpperCase()} · code ${code}`;
    this.copyBtn.style.display = "inline-block";
  }

  private copyRoomCode(): void {
    if (!this.roomCode) return;
    navigator.clipboard?.writeText(this.roomCode).catch(() => {});
    const original = this.copyBtn.textContent;
    this.copyBtn.textContent = "Copied!";
    setTimeout(() => { this.copyBtn.textContent = original; }, 1200);
  }

  /** Refresh from the latest state. `players` is room.state.players (MapSchema). */
  update(me: any, players: any, state: any, localId: string, nowMs: number): void {
    const pct = me ? Math.max(0, Math.min(1, me.hp / me.maxHp)) : 0;
    this.hpFill.style.width = `${pct * 100}%`;
    this.hpText.textContent = me ? `${Math.ceil(me.hp)}/${me.maxHp} HP` : "";
    this.killsChip.textContent = me ? `${me.kills} kills` : "0 kills";
    this.scoreChip.textContent = me ? `${Math.round(me.score)} pts` : "0 pts";

    // Player list (sorted by score).
    const rows: Array<[string, any]> = [];
    players?.forEach((p: any, id: string) => rows.push([id, p]));
    rows.sort((a, b) => (b[1].score as number) - (a[1].score as number));
    this.playerList.textContent = rows
      .slice(0, 8)
      .map(([id, p]) => `${(p.name as string).slice(0, 12)}${id === localId ? " (you)" : ""}  ${Math.round(p.score)}`)
      .join("\n");

    // Objective.
    if (state?.mode === "coop") {
      this.objective.textContent = `Wave ${state?.wave ?? 0}`;
    } else if (state?.mode === "koth") {
      const sec = Math.ceil((state?.timer ?? 0) / 1000);
      const holderId = state?.hillHolderId as string | undefined;
      let status = "Hill: unclaimed";
      if (holderId) {
        const p = players?.get(holderId);
        status = holderId === localId ? "Hill: YOU" : `Hill: ${p ? String(p.name).slice(0, 10) : "?"}`;
      }
      this.objective.textContent = `${status} · ${sec}s`;
    } else {
      const sec = Math.ceil((state?.timer ?? 0) / 1000);
      this.objective.textContent = `Deathmatch · ${sec}s`;
    }

    // Respawn countdown (approximate from local clock when dead).
    if (me && !me.alive) {
      this.respawn.style.display = "block";
      this.respawn.textContent = "Destroyed — respawning…";
    } else {
      this.respawn.style.display = "none";
    }

    this.updateAbility(me, state);
    this.drawMinimap(players, state, localId);
    void nowMs;
  }

  /** Top-down arena map: bounds, obstacles, active power-ups, all tanks. */
  private drawMinimap(players: any, state: any, localId: string): void {
    const ctx = this.minimapCtx;
    const size = this.minimapCanvas.width;
    const scale = size / (ARENA.HALF * 2);
    const toMap = (x: number, z: number): [number, number] => [size / 2 + x * scale, size / 2 + z * scale];

    ctx.clearRect(0, 0, size, size);
    ctx.fillStyle = "rgba(20,26,32,0.55)";
    ctx.fillRect(0, 0, size, size);

    // Static obstacles.
    ctx.fillStyle = "rgba(255,255,255,0.28)";
    for (const o of OBSTACLES) {
      const [cx, cz] = toMap(o.x, o.z);
      ctx.fillRect(cx - (o.w * scale) / 2, cz - (o.d * scale) / 2, Math.max(2, o.w * scale), Math.max(2, o.d * scale));
    }

    // Power-ups (only while active/on the ground).
    const puColor: Record<string, string> = { heal: "#2ecc71", speed: "#3498db", rapid: "#f1c40f", shield: "#9b5de5" };
    state?.powerups?.forEach((p: any) => {
      if (!p.active) return;
      const [cx, cz] = toMap(p.x, p.z);
      ctx.fillStyle = puColor[p.type] ?? "#ffffff";
      ctx.beginPath();
      ctx.arc(cx, cz, 2.5, 0, Math.PI * 2);
      ctx.fill();
    });

    // Tanks — local player drawn larger with a facing tick, everyone else a plain dot.
    players?.forEach((p: any, id: string) => {
      if (!p.alive) return;
      const [cx, cz] = toMap(p.x, p.z);
      const color = `#${(p.color >>> 0).toString(16).padStart(6, "0")}`;
      const isLocal = id === localId;
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.arc(cx, cz, isLocal ? 4 : 3, 0, Math.PI * 2);
      ctx.fill();
      if (isLocal) {
        ctx.strokeStyle = "#ffffff";
        ctx.lineWidth = 1.5;
        ctx.stroke();
        const fx = Math.sin(p.rotY as number);
        const fz = Math.cos(p.rotY as number);
        ctx.beginPath();
        ctx.moveTo(cx, cz);
        ctx.lineTo(cx + fx * 8, cz + fz * 8);
        ctx.strokeStyle = "#ffffff";
        ctx.lineWidth = 2;
        ctx.stroke();
      }
    });

    ctx.strokeStyle = "rgba(255,255,255,0.35)";
    ctx.lineWidth = 1;
    ctx.strokeRect(0.5, 0.5, size - 1, size - 1);
  }

  /** Ability cooldown card: radial sweep from server clock + abilityCdUntil. */
  private updateAbility(me: any, state: any): void {
    if (!me) { this.abilityWrap.style.display = "none"; return; }
    const def = getClass(me.classId).ability;
    if (!def || def.id === "none" || !isFinite(def.cooldownMs)) {
      this.abilityWrap.style.display = "none";
      return;
    }
    this.abilityWrap.style.display = "flex";
    this.abilityName.textContent = def.name;
    const total = def.cooldownMs;
    const clock = +(state?.clock ?? 0);
    const remaining = Math.max(0, (+me.abilityCdUntil) - clock);
    const ready = remaining <= 0;
    const pct = ready ? 100 : (1 - remaining / total) * 100;
    this.abilityIcon.style.background = `conic-gradient(#2a9d8f ${pct}%, rgba(255,255,255,0.08) ${pct}%)`;
    this.abilityState.textContent = ready ? "READY" : `${(remaining / 1000).toFixed(1)}s`;
    this.abilityState.style.color = ready ? "#2a9d8f" : "#9aa7ad";
    this.abilityKey.style.opacity = ready ? "1" : "0.5";
  }

  showRoundEnd(reason: string): void {
    this.roundEnd.style.display = "block";
    // `reason` can embed a player-chosen name (e.g. "<name> wins with N kills!")
    // — never inject it via innerHTML (stored-XSS risk). Build the DOM safely
    // and let textContent escape whatever the player typed as their name.
    this.roundEnd.textContent = "";
    this.roundEnd.append(document.createTextNode(reason), document.createElement("br"));
    const sub = document.createElement("span");
    Object.assign(sub.style, { fontSize: "14px", opacity: ".7" });
    sub.textContent = "Refresh / rejoin to play again";
    this.roundEnd.appendChild(sub);
  }

  hideRoundEnd(): void {
    this.roundEnd.style.display = "none";
  }

  /** Coop-only: a brief fading banner announcing a new wave, with a flavor
   * subtitle the first time a bot variant unlocks (see COOP.*_UNLOCK_WAVE) so
   * the escalating-difficulty system actually reads as a telegraphed threat
   * instead of a silent stat change. */
  showWaveBanner(wave: number): void {
    if (wave < 1) return; // initial state snapshot (no wave started yet)
    this.waveBannerMain.textContent = `WAVE ${wave}`;
    if (wave === COOP.RICOCHET_UNLOCK_WAVE) this.waveBannerSub.textContent = "New threat: Ricochet Bots";
    else if (wave === COOP.FAST_UNLOCK_WAVE) this.waveBannerSub.textContent = "New threat: Fast Bots";
    else this.waveBannerSub.textContent = "";
    this.waveBanner.style.opacity = "1";
    clearTimeout(this.waveBannerTimer);
    this.waveBannerTimer = setTimeout(() => { this.waveBanner.style.opacity = "0"; }, 2600);
  }
}
